import React, { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { Formik, FormikProps, useFormikContext } from "formik";
import * as Yup from "yup";
import Box from "themeComponents/Box";
import Button from "themeComponents/Button";
import FormikInput from "components/formikInput";
import SupportFileUpload from "./SupportFileUpload";
import { SupportAttachmentInput } from "types/support";

interface CreateTicketDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: {
    subject: string;
    message: string;
    attachments: SupportAttachmentInput[];
  }) => Promise<void>;
  onUpload: (file: File) => Promise<SupportAttachmentInput>;
}

interface FormValues {
  subject: string;
  message: string;
  attachments: SupportAttachmentInput[];
}

const emptyValues: FormValues = { subject: "", message: "", attachments: [] };

const schema = Yup.object({
  subject: Yup.string().trim().required("Subject is required").max(500),
  message: Yup.string().trim().required("Message is required"),
});

function CreateTicketAttachments({
  onUpload,
  disabled,
}: {
  onUpload: (file: File) => Promise<SupportAttachmentInput>;
  disabled: boolean;
}) {
  const { values, setFieldValue } = useFormikContext<FormValues>();

  return (
    <SupportFileUpload
      label="Attachments"
      attachments={values.attachments}
      onRemove={(index) =>
        setFieldValue(
          "attachments",
          values.attachments.filter((_, i) => i !== index)
        )
      }
      onUpload={async (file) => {
        const uploaded = await onUpload(file);
        if (uploaded?.fileUrl) {
          await setFieldValue("attachments", [...values.attachments, uploaded]);
        }
        return uploaded;
      }}
      disabled={disabled}
    />
  );
}

function CreateTicketDialog({
  open,
  onClose,
  onSubmit,
  onUpload,
}: CreateTicketDialogProps): React.JSX.Element {
  const [submitting, setSubmitting] = useState(false);
  const formikRef = React.useRef<FormikProps<FormValues>>(null);

  const handleClose = () => {
    formikRef.current?.resetForm();
    setSubmitting(false);
    onClose();
  };

  useEffect(() => {
    if (open) {
      setSubmitting(false);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEnforceFocus
      disableRestoreFocus
      data-testid="support-create-dialog"
    >
      <DialogTitle>Create support ticket</DialogTitle>
      {open ? (
        <Formik<FormValues>
          innerRef={formikRef}
          initialValues={emptyValues}
          validationSchema={schema}
          onSubmit={async (values, { resetForm }) => {
            setSubmitting(true);
            try {
              await onSubmit(values);
              resetForm();
              handleClose();
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ handleSubmit, isSubmitting, isValid, dirty }) => (
            <>
              <DialogContent>
                <Box display="flex" flexDirection="column" gap={2}>
                  <FormikInput
                    name="subject"
                    label="Subject *"
                    placeholder="Brief summary of your issue"
                    size="large"
                  />
                  <FormikInput
                    name="message"
                    label="Description *"
                    placeholder="Describe your issue in detail..."
                    multiline
                    rows={4}
                    size="large"
                  />
                  <CreateTicketAttachments onUpload={onUpload} disabled={submitting} />
                </Box>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                  type="button"
                  color="secondary"
                  onClick={handleClose}
                  disabled={submitting || isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  color="info"
                  variant="gradient"
                  loading={submitting || isSubmitting}
                  disabled={!dirty || !isValid || submitting || isSubmitting}
                  onClick={() => handleSubmit()}
                  data-testid="support-submit-ticket"
                >
                  Create ticket
                </Button>
              </DialogActions>
            </>
          )}
        </Formik>
      ) : null}
    </Dialog>
  );
}

export default CreateTicketDialog;
